/**
 * Webhooks — Provider + hook compartilhados (fonte única de verdade).
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Webhook } from '../../../../types';
import { webhooksRepository } from '../../../../infrastructure/repositories/SupabaseWebhooksRepository';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { createModuleLogger } from '../../../../utils/logger';

const log = createModuleLogger('[Webhooks]');

interface WebhooksContextData {
    webhooks: Webhook[];
    loading: boolean;
    createWebhook: (data: Omit<Webhook, 'id' | 'createdAt'>) => Promise<void>;
    updateWebhook: (id: string, data: Partial<Webhook>) => Promise<void>;
    deleteWebhook: (id: string) => Promise<void>;
    deleteBulk: (ids: string[]) => Promise<void>;
    selectedIds: Set<string>;
    toggleSelection: (id: string) => void;
    toggleAllSelection: (filteredWebhooks: Webhook[]) => void;
    clearSelection: () => void;
    refetch: () => Promise<void>;
}

const WebhooksContext = createContext<WebhooksContextData | undefined>(undefined);

export const WebhooksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [webhooks, setWebhooks] = useState<Webhook[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const loadWebhooks = useCallback(async () => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const data = await webhooksRepository.findAll(currentOrganization.id);
            setWebhooks(data);
        } catch (error) {
            log.error('Error loading webhooks:', error);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        if (currentOrganization?.id) {
            loadWebhooks();
        }
    }, [currentOrganization?.id, loadWebhooks]);

    const createWebhook = useCallback(async (data: Omit<Webhook, 'id' | 'createdAt'>) => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const newWebhook = await webhooksRepository.create(data, currentOrganization.id);
            setWebhooks(prev => [newWebhook, ...prev]);
        } catch (error) {
            log.error('Error creating webhook:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const updateWebhook = useCallback(async (id: string, data: Partial<Webhook>) => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const updatedWebhook = await webhooksRepository.update(id, data, currentOrganization.id);
            setWebhooks(prev => prev.map(webhook => (webhook.id === id ? updatedWebhook : webhook)));
        } catch (error) {
            log.error('Error updating webhook:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const deleteWebhook = useCallback(async (id: string) => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            await webhooksRepository.delete(id, currentOrganization.id);
            setWebhooks(prev => prev.filter(webhook => webhook.id !== id));
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        } catch (error) {
            log.error('Error deleting webhook:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAllSelection = useCallback((filteredWebhooks: Webhook[]) => {
        setSelectedIds(prev => {
            if (prev.size === filteredWebhooks.length && filteredWebhooks.length > 0) {
                return new Set();
            }
            return new Set(filteredWebhooks.map(w => w.id));
        });
    }, []);

    const deleteBulk = useCallback(async (ids: string[]) => {
        if (!currentOrganization?.id || ids.length === 0) return;

        setLoading(true);
        try {
            await Promise.all(ids.map(id => webhooksRepository.delete(id, currentOrganization.id!)));
            setWebhooks(prev => prev.filter(webhook => !ids.includes(webhook.id)));
            setSelectedIds(new Set());
        } catch (error) {
            log.error('Error deleting webhooks:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const value: WebhooksContextData = {
        webhooks,
        loading,
        createWebhook,
        updateWebhook,
        deleteWebhook,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refetch: loadWebhooks,
    };

    return React.createElement(WebhooksContext.Provider, { value }, children);
};

export const useWebhooks = (): WebhooksContextData => {
    const context = useContext(WebhooksContext);
    if (!context) {
        throw new Error('useWebhooks must be used within WebhooksProvider');
    }
    return context;
};
